import { __, sprintf } from '@wordpress/i18n';

import { Typography } from '@/components';
import { ArrowRightLineIcon } from '@/components/icons/react';

import styles from './AnnouncementBar.module.scss';

import type { HeaderData } from '../../types';

type AnnouncementBarProps = {
  announcement?: HeaderData['announcement'];
};

export function AnnouncementBar({ announcement }: AnnouncementBarProps) {
  const message = announcement?.text?.trim();

  const linkLabel = announcement?.linkLabel?.trim();
  const linkUrl = announcement?.linkUrl?.trim();
  const link =
    linkLabel && linkUrl
      ? {
          label: linkLabel,
          url: linkUrl,
          ariaLabel: message
            ? sprintf(__('%1$s – %2$s', 'interact-26'), linkLabel, message)
            : linkLabel,
        }
      : null;

  const announcementAriaLabel = __('Brand announcement', 'interact-26');

  const handleLinkClick = () => {
    if (!link) {
      return;
    }
    window.InteractTracking?.track('sticky_bar_cta_click', {
      link_url: link.url,
      link_text: link.label,
      page_name: window.InteractTracking?.getContext().page_name || document.title,
    });
  };

  return (
    <section
      className={`${styles.announcement} ${announcement?.style === 'gradient' ? '' : styles.solid} interact-header__announcement`}
      role="status"
      aria-label={announcementAriaLabel}
      aria-live="polite"
    >
      <div className={styles.inner}>
        {message && (
          <Typography
            singleLine={true}
            variant="body"
            color="var(--announcement-text)"
            size="small"
          >
            {message}
          </Typography>
        )}

        {link && (
          <a className={styles.link} href={link.url} onClick={handleLinkClick}>
            <Typography size="small" tagName="span" color="var(--announcement-link)" singleLine>
              <strong>{link.label}</strong>
              {message && <span className={styles.srOnly}> — {message}</span>}
            </Typography>
            <ArrowRightLineIcon
              className={`${styles.icon} ix-icon-18`}
              // Intrinsic size so it can't paint full-width before the header CSS applies.
              width={18}
              height={18}
              aria-hidden="true"
              color="var(--announcement-link)"
              focusable="false"
            />
          </a>
        )}
      </div>
    </section>
  );
}
